import { useEffect, useRef, useState } from "react";
import type { AccentColor, AgentSettings } from "../../shared/contracts";
import { SCREEN_HEIGHT, SCREEN_WIDTH, type AgentPointerEvent, type PointerFrame } from "../../shared/pointer";
import { wsOrigin } from "../lib/api";
import { ACCENT_OPTIONS } from "../lib/format";
import { AgentAvatarView } from "./avatar";
import { cx } from "./ui";

/** The agent shown by the cursors of a live view: its name, face and color (« Ton agent » / « Mes agents »). */
export interface CursorAgent {
  name: string;
  avatar: AgentSettings["avatar"];
  accentColor?: AccentColor | null;
  /** The drawn companion shown when there is no avatar image (« Ton agent »). */
  character?: AgentSettings["character"];
}

/** A cursor that has not acted for this long fades out (it comes back on its next pointed action)… */
const IDLE_MS = 60_000;
/** …and is dropped after this long. */
const GONE_MS = 5 * 60_000;
/** A cursor hidden for less than this glides from where it was to its next point; after that it fades in there. */
const RECENT_MS = 15_000;
const RECONNECT_MS = [1_000, 3_000, 8_000, 15_000];
/** A glide's length follows Fitts's law (a + b·log2(1 + d/w), d in screen pixels), bounded. */
const GLIDE_MIN_MS = 250;
const GLIDE_MAX_MS = 450;
/** With prefers-reduced-motion: a short straight hop, faded out then in. */
const HOP_MS = 220;
/** The share of a glide spent on the curve; the rest settles the small overshoot back onto the point. */
const CURVE_SHARE = 0.85;
/**
 * Steps that arrive while the cursor still glides are played in turn, each long enough to be seen: a point it lands
 * on (its click) shows at least SHOW_HOLD_MS before a fade (scroll, typing), and a fade lasts at least HIDE_HOLD_MS
 * before the next point brings it back.
 */
const SHOW_HOLD_MS = 250;
const HIDE_HOLD_MS = 200;
/** More steps than this waiting: only clicks, fades and the latest point are kept. */
const BACKLOG = 6;

type PointerAction = AgentPointerEvent["action"];
interface Point {
  x: number;
  y: number;
}

/** What a cursor does next: point somewhere (glide there, then click…), or fade out. */
type Step = { kind: "point"; x: number; y: number; action: PointerAction } | { kind: "hide"; action: PointerAction | null; ended?: true };

/** One glide from `from` to `to`: a cubic Bézier bowed to one side, ending a few pixels past `to`, then settling. */
interface Glide {
  from: Point;
  c1: Point;
  c2: Point;
  past: Point;
  to: Point;
  start: number;
  ms: number;
  /** prefers-reduced-motion: no curve, a fade out at `from` and in at `to`. */
  hop: boolean;
  click: boolean;
}

/** A cursor's motion, kept outside React (animated frame by frame on its element). */
interface Track {
  actor: string;
  order: number;
  /** Where it is drawn now (screen pixels); null before its first point. */
  pos: Point | null;
  glide: Glide | null;
  queue: Step[];
  visible: boolean;
  hiddenAt: number;
  /** When it last landed on a point (or appeared there). */
  shownAt: number;
  /** While its last point or fade is held (SHOW_HOLD_MS, HIDE_HOLD_MS): when its next step may play; 0 otherwise. */
  holdUntil: number;
  lastAt: number;
  el: HTMLDivElement | null;
}

/** What React draws for a cursor. */
interface CursorView {
  actor: string;
  order: number;
  /** Its last pointed target (screen pixels). */
  x: number;
  y: number;
  action: PointerAction;
  visible: boolean;
  moving: boolean;
  /** Bumped on every click as it lands: replays the ripple. */
  clicks: number;
}

const CLICKS: ReadonlySet<PointerAction> = new Set(["click", "double_click", "right_click"]);
/** Actions that do not point anywhere: the cursor fades out (a scroll, typing, a key). */
const UNPOINTED: ReadonlySet<PointerAction> = new Set(["scroll", "type"]);

/** The agent's own color first, then the other accents: each agent working on one screen gets its own. */
function colorFor(agent: CursorAgent, order: number): string {
  const own = ACCENT_OPTIONS.find((o) => o.value === agent.accentColor) ?? ACCENT_OPTIONS[0]!;
  const others = ACCENT_OPTIONS.filter((o) => o.value !== own.value && o.value !== "graphite");
  return order === 0 ? own.swatch : others[(order - 1) % others.length]!.swatch;
}

/** `hex` (#rrggbb) mixed with white (amount > 0) or black (amount < 0). */
function shade(hex: string, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const target = amount > 0 ? 255 : 0;
  const a = Math.abs(amount);
  const ch = (v: number) => Math.round(v + (target - v) * a);
  const [r, g, b] = [ch((n >> 16) & 255), ch((n >> 8) & 255), ch(n & 255)];
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/** Minimum-jerk profile: starts and ends at rest, the way a hand moves a mouse. */
function smooth(t: number): number {
  return t * t * t * (10 - 15 * t + 6 * t * t);
}

function bezier(g: Glide, p: number): Point {
  const q = 1 - p;
  const a = q * q * q;
  const b = 3 * q * q * p;
  const c = 3 * q * p * p;
  const d = p * p * p;
  return { x: a * g.from.x + b * g.c1.x + c * g.c2.x + d * g.past.x, y: a * g.from.y + b * g.c1.y + c * g.c2.y + d * g.past.y };
}

function plan(from: Point, to: Point, now: number, click: boolean, hop: boolean, hurry: boolean): Glide {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.hypot(dx, dy);
  const fitts = Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, GLIDE_MIN_MS + 36 * Math.log2(1 + dist / 30)));
  const ms = hop ? HOP_MS : hurry ? fitts * 0.6 : fitts;
  const ux = dist ? dx / dist : 0;
  const uy = dist ? dy / dist : 0;
  // The arc bows to one side, picked once for this glide; long glides bow more (bounded), tiny ones not at all.
  const side = Math.random() < 0.5 ? -1 : 1;
  const bow = hop || dist < 12 ? 0 : side * Math.min(150, dist * (0.12 + Math.random() * 0.1));
  const over = hop || dist < 40 ? 0 : Math.min(9, dist * 0.03);
  const past = { x: to.x + ux * over, y: to.y + uy * over };
  const [nx, ny] = [-uy, ux];
  const c1 = { x: from.x + (past.x - from.x) * 0.25 + nx * bow, y: from.y + (past.y - from.y) * 0.25 + ny * bow };
  const c2 = { x: from.x + (past.x - from.x) * 0.7 + nx * bow * 0.6, y: from.y + (past.y - from.y) * 0.7 + ny * bow * 0.6 };
  return { from, c1, c2, past, to, start: now, ms, hop, click };
}

/** Where a glide is at `now`, its inner fade (hops only) and whether it has landed. */
function glideAt(g: Glide, now: number): { p: Point; fade: number; done: boolean } {
  const t = Math.min(1, Math.max(0, (now - g.start) / g.ms));
  if (g.hop) return { p: t < 0.5 ? g.from : g.to, fade: Math.abs(1 - 2 * t), done: t >= 1 };
  if (t < CURVE_SHARE) return { p: bezier(g, smooth(t / CURVE_SHARE)), fade: 1, done: false };
  const s = (t - CURVE_SHARE) / (1 - CURVE_SHARE);
  const e = 1 - (1 - s) * (1 - s);
  return { p: { x: g.past.x + (g.to.x - g.past.x) * e, y: g.past.y + (g.to.y - g.past.y) * e }, fade: 1, done: t >= 1 };
}

/** Draws a cursor at `p` (screen pixels, scaled to the live view); `data-px`/`data-py` say where it is drawn now. */
function place(el: HTMLDivElement | null, p: Point | null, fade = 1): void {
  if (!el || !p) return;
  el.style.transform = `translate(${(p.x / SCREEN_WIDTH) * 100}cqw, ${(p.y / SCREEN_HEIGHT) * 100}cqh)`;
  el.style.setProperty("--hop", String(fade));
  el.dataset.px = String(Math.round(p.x * 10) / 10);
  el.dataset.py = String(Math.round(p.y * 10) / 10);
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * The agents' cursors over a live view (src/shared/pointer.ts): MachineDO says where each agent acts on desktop
 * `desktop` (clicks, typing, scrolling — computer-use actions and DevTools clicks alike). Each agent gets a
 * soft, rounded arrow in its color with its face and name. It stays where it last pointed and glides to its next point
 * along a slight curve (quick, eased, a tiny overshoot), then plays its click; a scroll, typing or an action without a
 * point fades it out until it points again; it leaves when its run ends or after a minute idle. The owner's own cursor
 * is noVNC's, untouched. `dimmed`: the owner holds the controls (the agents wait; their cursors turn grey).
 */
export function AgentCursors({ machineId, desktop, agent, live, dimmed }: { machineId: string; desktop: number; agent: CursorAgent; live: boolean; dimmed: boolean }) {
  const [views, setViews] = useState<Record<string, CursorView>>({});
  const tracks = useRef(new Map<string, Track>());

  useEffect(() => {
    setViews({});
    tracks.current.clear();
    if (!live) return;
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let frame: number | null = null;
    let orders = 0;

    const show = (t: Track, patch: Partial<CursorView>, click = false) =>
      setViews((all) => {
        const prev = all[t.actor] ?? { actor: t.actor, order: t.order, x: 0, y: 0, action: "move" as PointerAction, visible: false, moving: false, clicks: 0 };
        return { ...all, [t.actor]: { ...prev, ...patch, clicks: prev.clicks + (click ? 1 : 0) } };
      });

    // Runs a cursor's next steps until one takes time (a glide) or none is left.
    const pump = (t: Track, now: number) => {
      while (!t.glide && t.queue.length) {
        const step = t.queue[0]!;
        // Landed or faded out just now: it stays so a moment before the next step (tick resumes it).
        const until = step.kind === "point" ? (t.visible ? 0 : t.hiddenAt + HIDE_HOLD_MS) : t.visible ? t.shownAt + SHOW_HOLD_MS : 0;
        if (until > now) {
          t.holdUntil = until;
          break;
        }
        t.queue.shift();
        if (step.kind === "hide") {
          // Its run ended: a later point (rare) fades in where it lands rather than gliding from here.
          if (step.ended) t.hiddenAt = -Infinity;
          else if (t.visible) t.hiddenAt = now;
          t.visible = false;
          show(t, { visible: false, moving: false, ...(step.action ? { action: step.action } : {}) });
          continue;
        }
        const to = { x: step.x, y: step.y };
        const click = CLICKS.has(step.action);
        const fresh = !t.pos || (!t.visible && now - t.hiddenAt > RECENT_MS);
        const near = t.pos ? Math.hypot(to.x - t.pos.x, to.y - t.pos.y) < 1.5 : false;
        t.visible = true;
        if (fresh || near) {
          // Its first point, or long gone: it fades in right there.
          t.pos = to;
          t.shownAt = now;
          place(t.el, to);
          show(t, { x: to.x, y: to.y, action: step.action, visible: true, moving: false }, click);
          continue;
        }
        t.glide = plan(t.pos!, to, now, click, reducedMotion(), t.queue.length >= 2);
        show(t, { x: to.x, y: to.y, action: step.action, visible: true, moving: true });
      }
    };

    const tick = () => {
      frame = null;
      const now = performance.now();
      let busy = false;
      for (const t of tracks.current.values()) {
        const g = t.glide;
        if (!g) {
          if (t.holdUntil && now >= t.holdUntil) {
            t.holdUntil = 0;
            pump(t, now);
          }
          if (t.glide || t.holdUntil) busy = true;
          continue;
        }
        const { p, fade, done } = glideAt(g, now);
        t.pos = p;
        place(t.el, p, fade);
        if (!done) {
          busy = true;
          continue;
        }
        // Landed: the click plays here, then the next step.
        t.glide = null;
        t.pos = g.to;
        t.shownAt = now;
        place(t.el, g.to);
        show(t, { moving: false }, g.click);
        pump(t, now);
        if (t.glide || t.holdUntil) busy = true;
      }
      if (busy) frame = requestAnimationFrame(tick);
    };
    const animate = () => {
      if (frame === null && [...tracks.current.values()].some((t) => t.glide || t.holdUntil)) frame = requestAnimationFrame(tick);
    };

    const receive = (f: PointerFrame) => {
      if (f.type === "pointer_hide") {
        if (f.desktop !== null && f.desktop !== desktop) return;
        const t = tracks.current.get(f.actor);
        if (!t) return;
        t.queue.push(f.reason === "done" ? { kind: "hide", action: null, ended: true } : { kind: "hide", action: null });
        pump(t, performance.now());
        animate();
        return;
      }
      if (f.type !== "pointer" || f.desktop !== desktop) return;
      let t = tracks.current.get(f.actor);
      if (!t) {
        t = { actor: f.actor, order: orders++, pos: null, glide: null, queue: [], visible: false, hiddenAt: -Infinity, shownAt: -Infinity, holdUntil: 0, lastAt: 0, el: null };
        tracks.current.set(f.actor, t);
      }
      t.lastAt = Date.now();
      t.queue.push(UNPOINTED.has(f.action) ? { kind: "hide", action: f.action } : { kind: "point", x: f.x, y: f.y, action: f.action });
      if (t.queue.length > BACKLOG) t.queue = t.queue.filter((s, i, all) => i === all.length - 1 || s.kind === "hide" || CLICKS.has(s.action));
      pump(t, performance.now());
      animate();
    };

    const open = () => {
      ws = new WebSocket(`${wsOrigin()}/api/machines/${encodeURIComponent(machineId)}/pointer?desktop=${desktop}`);
      ws.onopen = () => {
        attempt = 0;
      };
      ws.onmessage = (e) => {
        let f: PointerFrame;
        try {
          f = JSON.parse(String(e.data)) as PointerFrame;
        } catch {
          return;
        }
        receive(f);
      };
      ws.onclose = () => {
        ws = null;
        if (stopped) return;
        retry = setTimeout(open, RECONNECT_MS[Math.min(attempt++, RECONNECT_MS.length - 1)]);
      };
    };
    open();

    // Idle cursors fade out after a minute, and are dropped after five.
    const sweep = setInterval(() => {
      const now = Date.now();
      for (const t of [...tracks.current.values()]) {
        if (t.glide || t.queue.length) continue;
        if (now - t.lastAt > GONE_MS) {
          tracks.current.delete(t.actor);
          setViews((all) => {
            const rest = { ...all };
            delete rest[t.actor];
            return rest;
          });
        } else if (t.visible && now - t.lastAt > IDLE_MS) {
          t.visible = false;
          t.hiddenAt = -Infinity;
          show(t, { visible: false });
        }
      }
    }, 5_000);

    return () => {
      stopped = true;
      clearInterval(sweep);
      if (retry) clearTimeout(retry);
      if (frame !== null) cancelAnimationFrame(frame);
      ws?.close();
    };
  }, [machineId, desktop, live]);

  const shown = Object.values(views);
  if (!live || !shown.length) return null;
  const several = shown.filter((c) => c.visible).length > 1;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ containerType: "size" }} data-testid="agent-cursors" aria-hidden="true">
      {shown.map((c) => {
        const color = colorFor(agent, c.order);
        const label = c.actor === "agent" || !several ? agent.name : `${agent.name} · ${c.order + 1}`;
        return (
          <div
            key={c.actor}
            ref={(el) => {
              const t = tracks.current.get(c.actor);
              if (!t) return;
              t.el = el;
              if (el && !t.glide) place(el, t.pos);
            }}
            data-testid="agent-cursor"
            data-actor={c.actor}
            data-action={c.action}
            data-x={c.x}
            data-y={c.y}
            data-visible={c.visible ? "true" : "false"}
            data-moving={c.moving ? "true" : "false"}
            data-color={color}
            data-dimmed={dimmed ? "true" : "false"}
            className={cx("agent-cursor absolute top-0 left-0", dimmed && "grayscale")}
            style={{ opacity: c.visible ? (dimmed ? 0.35 : 1) : 0, ["--cursor" as string]: color }}
          >
            <div className="agent-cursor-body">
              {c.clicks > 0 ? <span key={c.clicks} className="agent-cursor-ripple" /> : null}
              <CursorArrow color={color} />
              <span className="agent-cursor-label" style={{ background: `linear-gradient(160deg, ${shade(color, 0.2)}, ${shade(color, -0.12)})` }}>
                <AgentAvatarView agent={{ name: agent.name, avatar: agent.avatar, character: agent.character ?? null }} size={18} animated={false} />
                <span className="max-w-[9rem] truncate">{label}</span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The cursor itself: a rounded arrow in ONE flat color, the agent's (the owner's call: no ring at the tip, no white
 * inside), lifted by a soft shadow (styles.css). The arrow's tip, the point it acts on, sits at the element's origin
 * (styles.css offsets the drawing).
 */
function CursorArrow({ color }: { color: string }) {
  // Tip (5,5), right wing (33,16), notch (20,20), bottom wing (16,33): every corner rounded by the round joins of a
  // thick stroke of the same color.
  const arrow = "M5 5 L33 16 L20 20 L16 33 Z";
  return (
    <svg viewBox="0 0 40 40" className="agent-cursor-arrow" aria-hidden="true">
      <path d={arrow} fill={color} stroke={color} strokeWidth="6" strokeLinejoin="round" />
    </svg>
  );
}
